import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { Medications, SurgicalHistory } from 'src/app/beans/health-history/medical-history.bean';
import { MEDICAL_CONDITION } from 'src/app/constants/health-history.constants';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { LookupService } from 'src/app/services/ha-new/lookup.service';
import { MedicalHistoryService } from 'src/app/services/ha-new/medical-history.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

/**
 * medical conditions component
 */
@Component({
	selector: 'app-medical-conditions',
	templateUrl: './medical-conditions.component.html',
	styleUrls: ['./medical-conditions.component.scss'],
})
export class HAMedicalConditionsComponent implements OnInit, OnDestroy {
	/** holds medical conditions lookup data */
	medicalConditions: any = [];
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds medical conditions form */
	medicalConditionForm: FormGroup;
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	/** holds ha id */
	healthAssessmentID: number;
	/** holds past present medical conditions */
	pastPresentMedicalConditions: any = [];
	/** holds surgeries form control */
	surgeries: FormControl = new FormControl({
		value: '',
		disabled: this.readOnly,
	});
	/** holds medications form control */
	medications: FormControl = new FormControl({
		value: '',
		disabled: this.readOnly,
	});
	/** holds allergies form */
	allergiesForm: FormGroup;
	/** holds scroll values */
	scrollTo = '';
	/** to decide if text box can be shown */
	showCancerTextBox: boolean = false;
	/** to decide if text box can be shown */
	showOtherTextBox: boolean = false;
	/** holds medical condition enum */
	medicalConditionsEnum = MEDICAL_CONDITION;
	returningUser: boolean = false;

	/**
	 * constructor
	 * @param lookupService
	 * @param spinner
	 * @param activatedRoute
	 * @param router
	 */
	constructor(
		private lookupService: LookupService,
		private medicalHistoryService: MedicalHistoryService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private haService: NewHAService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.returningUser = params.returningUser === 'true';
			if (this.readOnly) {
				this.surgeries.disable();
				this.medications.disable();
			}
			if (this.scrollTo != 'false') this.scrollTo = params.section;
			this.healthAssessmentID = params.healthAssessmentID;
		});
	}

	/**
	 * on component initialization
	 */
	ngOnInit() {
		this.initializeMedicalCondition();
		this.initializeAllergiesForm();
		this.getValues();
	}

	getValues() {
		this.spinner.startLoader();
		forkJoin({
			lookup: this.lookupService.getMedicalConditionLookupValues(),
			getPastPresentMedicalConditions: this.medicalHistoryService.getPastPresentMedicalConditions(
				this.apptID,
				this.healthAssessmentID
			),
			getSurgicalHistory: this.medicalHistoryService.getSurgicalHistory(this.apptID, this.healthAssessmentID),
			getMedications: this.medicalHistoryService.getMedications(this.apptID, this.healthAssessmentID),
			getAllergies: this.medicalHistoryService.getAllergies(this.apptID, this.healthAssessmentID),
		}).subscribe(
			res => {
				this.medicalConditions = res.lookup.data;
				this.medicalConditions.forEach(condition => {
					condition['checked'] = false;
					// For type of cancer and other type hiding checkbox
					if (condition.id === MEDICAL_CONDITION.CANCER_TEXT || condition.id === MEDICAL_CONDITION.OTHER_REASON) {
						condition['hideCheckbox'] = true;
					}
				});
				this.getPastPresentMedicalConditions(res.getPastPresentMedicalConditions.data);

				this.medications.setValue(res.getMedications.data.medicationList);

				this.surgeries.setValue(res.getSurgicalHistory.data.surgeries);

				this.allergiesForm.controls.haveAllergies.setValue(res.getAllergies.data.haveAllergies);
				this.allergiesForm.controls.medication.setValue(res.getAllergies.data.medication);
				this.allergiesForm.controls.reaction.setValue(res.getAllergies.data.reaction);
				this.spinner.stopLoader();

				if (this.scrollTo != 'false') {
					if (this.haService.scrollView(this.scrollTo)) {
						this.scrollTo = 'false';
					}
				}
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${HAMedicalConditionsComponent.name}][${this.getValues.name}]`, 'Error occurred while saving past present medical conditions ', err);
			}
		);
	}

	/**
	 * initialize medical conditions form
	 */
	initializeMedicalCondition() {
		this.medicalConditionForm = new FormGroup({
			typeOfCancer: new FormControl({ value: '', disabled: this.readOnly }),
			medicalConditions: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
		});
	}

	/**
	 * initialize allergies form
	 */
	initializeAllergiesForm() {
		this.allergiesForm = new FormGroup({
			haveAllergies: new FormControl({ value: '', disabled: this.readOnly }),
			medication: new FormControl({ value: '', disabled: this.readOnly }),
			reaction: new FormControl({ value: '', disabled: this.readOnly }),
		});
	}

	/**
	 * gets past and present medical condition values
	 */
	getPastPresentMedicalConditions(data) {
		if (data && Array.isArray(data)) {
			data.forEach(condition => {
				this.pastPresentMedicalConditions.push(condition);
				const index = this.medicalConditions.findIndex(
					medicalCondition => medicalCondition.id === condition.conditionID
				);
				this.medicalConditions[index].checked = true;
				// if either cancer/other is selected setting flag to display rspective text boxes
				if (condition.conditionID === MEDICAL_CONDITION.CANCER) {
					this.showCancerTextBox = true;
				}
				if (condition.conditionID === MEDICAL_CONDITION.OTHER) {
					this.showOtherTextBox = true;
				}
				// if either cancer/other text box has values, setting form with respective values
				if (condition.conditionID === MEDICAL_CONDITION.CANCER_TEXT) {
					this.medicalConditionForm.controls.typeOfCancer.setValue(condition.optionValue);
				}
				if (condition.conditionID === MEDICAL_CONDITION.OTHER_REASON) {
					this.medicalConditionForm.controls.medicalConditions.setValue(condition.optionValue);
				}
			});
		}
	}

	/**
	 * on selecting medical condition
	 * @param condition
	 */
	medicalConditionSelection(event, condition) {
		console.log(event, condition);
		// Pushing into array if checked
		if (event.checked) {
			// showing text fields if cancer/other is selected
			if (condition.id === MEDICAL_CONDITION.CANCER) {
				this.showCancerTextBox = true;
			}
			if (condition.id === MEDICAL_CONDITION.OTHER) {
				this.showOtherTextBox = true;
			}
			this.pastPresentMedicalConditions.push({
				conditionID: condition.id,
				condition: condition.name,
			});
		} else {
			// removing from array if unchecked
			condition.checked = false;
			if (condition.id === MEDICAL_CONDITION.CANCER) {
				this.showCancerTextBox = false;
				// if cancer is unselected removing cancer text also from array
				const index = this.pastPresentMedicalConditions.findIndex(
					element => element.conditionID === MEDICAL_CONDITION.CANCER_TEXT
				);
				this.pastPresentMedicalConditions.splice(index, 1);
			}
			if (condition.id === MEDICAL_CONDITION.OTHER) {
				this.showOtherTextBox = false;
				// if other is unselected removing cancer text also from array
				const index = this.pastPresentMedicalConditions.findIndex(
					element => element.conditionID === MEDICAL_CONDITION.OTHER_REASON
				);
				this.pastPresentMedicalConditions.splice(index, 1);
			}
			const index = this.pastPresentMedicalConditions.findIndex(element => element.conditionID === condition.id);
			this.pastPresentMedicalConditions.splice(index, 1);
		}
	}

	/**
	 * Update form control value with empty string in case if value is assigned `null` or `undefined`
	 * @param control Form Control
	 */
	updateNullUndefinedFormValue(control: FormControl | AbstractControl): void {
		if (control.valid && control.touched && (control.value === null || control.value === undefined)) {
			control.setValue('');
		}
	}

	/**
	 * save data on clicking next
	 */
	next() {
		if (!this.readOnly) {
			this.savePastPresentMedicalConditions();
			if (this.surgeries.valid && this.surgeries.touched) {
				this.saveSurgicalHistory();
			}
			if (this.medications.valid && this.medications.touched) {
				this.saveMedications();
			}
			if (this.allergiesForm.valid && this.allergiesForm.touched) {
				this.saveAllergies();
			}
			this.haService.redirect(
				[],
				['screening'],
				[],
				this.apptID,
				this.healthAssessmentID,
				this.readOnly,
				'medical-screening',
				this.returningUser
			);
		}
	}

	/**
	 * saves past and present medical condition values
	 */
	savePastPresentMedicalConditions() {
		this.spinner.startLoader();
		// If the values of cancer/other text field changes, pushing these into conditions array
		const cancerIndex = this.pastPresentMedicalConditions.findIndex(
			condition => condition.conditionID == MEDICAL_CONDITION.CANCER
		);
		if (cancerIndex >= 0) {
			const index = this.pastPresentMedicalConditions.findIndex(
				condition => condition.conditionID == MEDICAL_CONDITION.CANCER_TEXT
			);
			if (index >= 0) {
				this.pastPresentMedicalConditions[index].optionValue = this.medicalConditionForm.controls.typeOfCancer.value;
			} else {
				const condition = this.medicalConditions.find(condition => condition.id == MEDICAL_CONDITION.CANCER_TEXT);
				this.pastPresentMedicalConditions.push({
					conditionID: condition.id,
					condition: condition.name,
					optionValue: this.medicalConditionForm.controls.typeOfCancer.value,
				});
			}
		}
		const otherIndex = this.pastPresentMedicalConditions.findIndex(
			condition => condition.conditionID == MEDICAL_CONDITION.OTHER
		);
		if (otherIndex >= 0) {
			const index = this.pastPresentMedicalConditions.findIndex(
				condition => condition.conditionID == MEDICAL_CONDITION.OTHER_REASON
			);
			if (index >= 0) {
				this.pastPresentMedicalConditions[index].optionValue =
					this.medicalConditionForm.controls.medicalConditions.value;
			} else {
				const condition = this.medicalConditions.find(condition => condition.id == MEDICAL_CONDITION.OTHER_REASON);
				this.pastPresentMedicalConditions.push({
					conditionID: condition.id,
					condition: condition.name,
					optionValue: this.medicalConditionForm.controls.medicalConditions.value,
				});
			}
		}
		this.medicalHistoryService
			.savePastPresentMedicalConditions(this.apptID, this.healthAssessmentID, {
				medicalDTO: this.pastPresentMedicalConditions,
			})
			.subscribe(
				res => {
					this.spinner.stopLoader();
					console.log(res);
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${HAMedicalConditionsComponent.name}][${this.savePastPresentMedicalConditions.name}]`, 'Error occurred while saving past present medical conditions ', err);
				}
			);
	}

	/**
	 * saves surgical history
	 */
	saveSurgicalHistory() {
		this.updateNullUndefinedFormValue(this.surgeries);
		const history = new SurgicalHistory();
		history.healthAssessmentID = this.healthAssessmentID;
		history.surgeries = this.surgeries.value;
		this.spinner.startLoader();
		this.medicalHistoryService.saveSurgicalHistory(this.apptID, this.healthAssessmentID, history).subscribe(
			res => {
				this.spinner.stopLoader();
				console.log(res);
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${HAMedicalConditionsComponent.name}][${this.saveSurgicalHistory.name}]`, 'Error occurred while saving surgical history ', err);
			}
		);
	}

	/**
	 * saves medications
	 */
	saveMedications() {
		this.updateNullUndefinedFormValue(this.medications);
		const medications = new Medications();
		medications.healthAssessmentID = this.healthAssessmentID;
		medications.medicationList = this.medications.value;
		this.spinner.startLoader();
		this.medicalHistoryService.saveMedications(this.apptID, this.healthAssessmentID, medications).subscribe(
			res => {
				this.spinner.stopLoader();
				console.log(res);
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${HAMedicalConditionsComponent.name}][${this.saveMedications.name}]`, 'Error occurred while saving medications ', err);
			}
		);
	}

	/**
	 * saves allergies
	 */
	saveAllergies() {
		if (!this.allergiesForm.controls.haveAllergies.value) {
			this.allergiesForm.controls.medication.setValue('');
			this.allergiesForm.controls.reaction.setValue('');
		} else {
			this.updateNullUndefinedFormValue(this.allergiesForm.controls.medication);
			this.updateNullUndefinedFormValue(this.allergiesForm.controls.reaction);
			if (!this.allergiesForm.controls.medication.value && !this.allergiesForm.controls.reaction.value) {
				this.allergiesForm.controls.haveAllergies.setValue(false);
			}
		}
		this.spinner.startLoader();
		this.medicalHistoryService
			.saveAllergies(this.apptID, this.healthAssessmentID, this.allergiesForm.getRawValue())
			.subscribe(
				res => {
					this.spinner.stopLoader();
					console.log(res);
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${HAMedicalConditionsComponent.name}][${this.saveAllergies.name}]`, 'Error occurred while saving allergies ', err);
				}
			);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
